import {type TimeZone} from '@hconnect/common/types'
import {dateFormatter, selectableTableRowSx} from '@hconnect/uikit'
import {Column, DataTable} from '@hconnect/uikit/src/lib2'
import {TFunction} from 'i18next'
import moment from 'moment-timezone'
import {useMemo} from 'react'

import {useWidgetTranslation} from '../hooks'
import {type ChecklistTemplate} from '../types'

type Props = {
  data?: ChecklistTemplate[]
  isLoading?: boolean
  emptyMessage?: string
  selectedRowId?: string
  onRowClick?: (item: ChecklistTemplate) => void
  timezone: TimeZone
}

export const ChecklistTemplatesTable = ({
  data,
  isLoading,
  emptyMessage,
  selectedRowId,
  onRowClick,
  timezone
}: Props) => {
  const {t} = useWidgetTranslation()
  const columns = useMemo(() => generateColumns({t, timezone}), [t, timezone])

  return (
    <DataTable<ChecklistTemplate>
      columns={columns}
      data={data}
      loading={isLoading}
      emptyMessage={emptyMessage}
      data-test-id={'checklist-templates-table'}
      onRowClick={(_, item) => onRowClick?.(item)}
      rowSx={({id}) => selectableTableRowSx(selectedRowId === id)}
    />
  )
}

const generateColumns = ({
  t,
  timezone
}: {
  t: TFunction
  timezone: string
}): Column<ChecklistTemplate>[] => [
  {
    key: 'name',
    label: t('label.checklistName')
  },
  {
    key: 'description',
    label: t('label.description')
  },
  {
    key: 'type',
    label: t('label.type'),
    customTemplate: ({type}) => t(`checklist.templateType.${type}`)
  },
  {
    key: 'category',
    label: t('label.category'),
    customTemplate: ({category}) => t(`checklist.templateCategory.${category}`)
  },
  {
    key: 'createdAt',
    label: t('label.addedToTheSystem'),
    customTemplate: ({createdAt}) => dateFormatter(moment.utc(createdAt).tz(timezone))
  }
]
